<template>
    <div>
        <b-row>
            <b-col>
                <strong>Patient Name: </strong>
                {{ patient.firstName + ', ' + patient.lastName }}
                <br>
                <strong>Phone Number: </strong>
                {{ patient.phoneNumber | formatPhone }}
                <template v-if="patient.cellNumber">
                    <br>
                    <strong>Cell Number: </strong>
                    {{ patient.cellNumber | formatPhone }}
                </template>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <EnumSelector v-model="newConsultation.type" :enum-codes="ConsultationType" label="Type" required />
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <EnumSelector v-model="newConsultation.category" :enum-codes="ConsultationCategory" label="Category" required />
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <strong>Notes:</strong>
                <div class="px-3">
                    <NotesListItem v-for="(note, index) in notes" :key="index" :note="note" :can-reply="false"
                        :can-pin="false" />
                </div>
                <NotesForm ref="noteForm" :emits-only="true" @note="handleNote" />
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <strong>Is the consultation clinical?<span>*</span></strong>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <b-form-radio v-model="newConsultation.clinical" :value="true">Yes</b-form-radio>
            </b-col>
            <b-col>
                <b-form-radio v-model="newConsultation.clinical" :value="false">No</b-form-radio>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <SearchComponent :id="prescription?.id" v-model="prescription"
                    :search-u-r-l="'/patient/' + patient.id + '/prescriptions'" prepopulate label="Prescription"
                    :option-label="(e) => `RxNumber: ${e.rxNumber} &middot; ${e.drugName}`"
                    :params="{ excludeRefills: true, storeId: store.id }" clearable />
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <hr>
                <b-form-group label="Question Set:" class="mb-3">
                    <b-select v-model="newConsultation.completionQuestionSetId" :options="questionSetOptions" />
                </b-form-group>

                <TaskQuestions
                    v-if="newConsultation.completionQuestionSetId === 0 || newConsultation.completionQuestionSetId"
                    :questions="questions"
                    @input="updateQuestions"
                    :editing="true"
                    :show-errors="showCompletionQuestionErrors"
                    :disabled="newConsultation.completionQuestionSetId > 0"
                    :entity-type="EntityType.Consultation" />
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <b-button variant="success" @click="saveNewConsultation"
                    :disabled="newConsultation.clinical == null || saveWasClicked">
                    Save
                </b-button>
            </b-col>
        </b-row>
    </div>
</template>

<script lang="ts">

import { Component, Vue, Prop, Watch, Ref } from 'vue-property-decorator';
import { Consultation, ConsultationCategory, ConsultationType } from '../../models/Consultation';
import Radio from "src/components/Inputs/Radio.vue";
import { Patient } from "@/models/Patient";
import { Store } from "@/models/Store";
import axios, { AxiosError } from 'axios';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import SearchComponent from '../SearchComponent.vue';
import { Prescription } from '@/models/Prescription';
import EnumSelector from '../EnumSelector.vue';
import TaskQuestions from '../Tasks/TaskQuestions.vue';
import { UserTaskQuestion, UserTaskQuestionSet, UserTaskQuestionType } from '@/models/UserTask';
import { EntityType } from '@/models/EntityType';
import NotesForm from '../Note/NotesForm.vue';
import { Note } from '@/models/Note';
import NotesListItem from '../Note/NotesListItem.vue';

@Component({
    name: "NewConsultationFormComponent",
    components: {
        [Radio.name]: Radio,
        SearchComponent,
        EnumSelector,
        TaskQuestions,
        NotesForm,
        NotesListItem
    }
})
export default class NewConsultationFormComponent extends Vue {
    EntityType = EntityType;

    @Prop() private patient!: Patient;
    @Prop() private store!: Store;
    @Prop({ default: null }) private prefilledPrescription!: Prescription | null;

    @Ref("noteForm") protected noteForm!: NotesForm;

    private newConsultation: Consultation = Object.assign(new Consultation(), {
        type: ConsultationType.General,
        category: ConsultationCategory.RN,
        note: "",
        status: 1,
        completionQuestionSetId: null
    });
    private questions: UserTaskQuestion[] = [];

    private prescription: Prescription | null = null;

    ConsultationType = ConsultationType;
    ConsultationCategory = ConsultationCategory;

    created() {
        this.newConsultation.storeId = this.store.id as number;
        this.newConsultation.patientId = this.patient.id;
        if (this.prefilledPrescription) {
            this.prescription = this.prefilledPrescription;
        }
        this.getQuestionSets();
    }

    private saveWasClicked = false;
    private showCompletionQuestionErrors = false;

    async saveNewConsultation() {
        this.saveWasClicked = true;
        this.showCompletionQuestionErrors = false;

        const hasCompletionQuestions = !!this.questions?.length;
        const canEditCompletionQuestions = true;

        // Check if Custom is selected but no questions added
        if (this.newConsultation.completionQuestionSetId === 0 && !hasCompletionQuestions) {
            this.$notification(NotificationOptions.error(
                "Please add at least one question for custom question sets, or select a different question set option."
            ));
            this.saveWasClicked = false;
            return false;
        }

        if (canEditCompletionQuestions && hasCompletionQuestions) {
            const questionsHaveErrors = this.questions.some(q => {
                if (!q.text?.trim()) return true;
                if (![UserTaskQuestionType.multipleChoiceMultiple, UserTaskQuestionType.multipleChoiceSingle].includes(q.typeId)) return false;
                if (q.options.some(o => !o.text?.trim())) return true;
                const uniqueTexts = new Set(q.options.map(o => o.text?.trim()));
                if (uniqueTexts.size !== q.options.length) return true;
                return false;
            });
            if (questionsHaveErrors) {
                this.$nextTick(() => {
                    this.showCompletionQuestionErrors = true;
                });
                this.$notification(NotificationOptions.error("Some questions have errors."));
                this.saveWasClicked = false;
                return false;
            }
        }

        try {
            await this.noteForm.save();

            await axios.post('/Consultation/', {
                ...this.newConsultation,
                rxNumber: this.prescription?.rxNumber,
                questions: this.questions,
                notes: this.notes
            });
            this.$notification(NotificationOptions.success("Consultation created successfully"));
            this.$emit('updateList');
            this.saveWasClicked = false;
        } catch (error) {
            this.$notification(NotificationOptions.errorSaveNotificationPreset("Consultation", error as AxiosError));
            console.log("Error creating consultation", error);
            this.saveWasClicked = false;
        }
    }

    private questionSets: UserTaskQuestionSet[] = [];

    async getQuestionSets() {
        try {
            const resp = await this.$http.get<UserTaskQuestionSet[]>(`/task/question-sets/${EntityType.Consultation}`);
            this.questionSets = resp.data.filter(qs => qs.name && qs.active);
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    @Watch("newConsultation.completionQuestionSetId")
    async getQuestionSet() {
        try {
            if (!this.newConsultation.completionQuestionSetId || this.newConsultation.completionQuestionSetId === 0) {
                this.questions = [];
                return;
            }
            const resp = await this.$http.get<UserTaskQuestionSet>(`/task/question-set/${this.newConsultation.completionQuestionSetId}`);
            this.questions = resp.data.questions;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    get questionSetOptions() {
        const options = [
            { value: null, text: 'None' },
            ...this.questionSets.map(qs => ({ value: qs.id, text: qs.name }))
        ];

        if (this.$permissions.some(p => p.permissionCodeName === 'Admin/Consultation Question Sets')) {
            options.push({ value: 0, text: 'Custom' });
        }

        return options;
    }

    protected notes: Note[] = [];

    updateQuestions(updatedQuestions: UserTaskQuestion[]) {
        this.questions = updatedQuestions;
    }

    handleNote(note: Note) {
        this.notes.unshift(note);
    }
}
</script>

<style scoped></style>
